import React from "react";
import { ArrowDownTrayIcon } from "@heroicons/react/24/solid";
import {
  Avatar,
  Card,
  CardBody,
  Chip,
  Divider,
  Button,
  Tooltip,
} from "@heroui/react";

import GeneralActivityTab from "./generalactivityTab";
import ComponentPaymentsTab from "./componentPaymentsTab";
import ResultRatingsTab from "./resultratingTab";
import { useNavigate } from "react-router-dom";

interface Props {
  deal: any;
  formData: any;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  t: any;
  isFighter: boolean;
  canPayComponents: boolean;
  handlePayComponent: (id: number) => void;
  currentUserId: string;
  isManager: boolean;
  hasUserRated: boolean;
  refreshDeal: () => void;
  downloadingAgreement: boolean;
  handleDownloadAgreement: () => void;
  renderDynamicObject: (obj: any, skip?: number) => React.ReactNode;
  renderSummary: (obj: any, maxFields?: number) => React.ReactNode;
  tr: (value?: string) => string; // 👈 புது prop type add பண்ணிட்டோம்
}

const HIDDEN_FIELD_KEYS = new Set([
  "opponentFighterId",
  "opponent_fighter_id",
  "OpponentFighterId",
]);

const SIDEBAR_SECTIONS = ["core", "details"];

const filterHiddenFields = (data: any): any => {
  if (!data || typeof data !== "object" || Array.isArray(data)) return data;
  return Object.fromEntries(
    Object.entries(data)
      .filter(([key]) => !HIDDEN_FIELD_KEYS.has(key))
      .map(([key, value]) => [key, filterHiddenFields(value)]),
  );
};

const DealViewContent: React.FC<Props> = ({
  deal,
  formData,
  activeTab,
  setActiveTab,
  t,
  isFighter,
  canPayComponents,
  handlePayComponent,
  currentUserId,
  isManager,
  hasUserRated,
  refreshDeal,
  downloadingAgreement,
  handleDownloadAgreement,
  renderDynamicObject,
  renderSummary,
  tr, // 👈 புது parameter destructure பண்ணிட்டோம்
}) => {
  const showResultTab = [
    "WAITING_FOR_RESULT",
    "RESULT_UPDATED",
    "RESULT_CONFIRMED",
    "FIGHTER_RATED",
    "MANAGER_RATED",
    "DISPUTE",
  ].includes(deal?.status);
  const currentUserRole: "MANAGER" | "FIGHTER" = isManager ? "MANAGER" : "FIGHTER";
const navigate = useNavigate();
  const navigateToProfile = (profileId?: number | string | null) => {
    if (!profileId) return;
    navigate(`/profile?profileId=${profileId}`);
  };
  const tabs = ["general", "componentPayments", "progress", "activity"];

  React.useEffect(() => {
    if (activeTab === "result" && !showResultTab) {
      setActiveTab("progress");
    }
  }, [activeTab, setActiveTab, showResultTab]);

  const sidebarFormData = React.useMemo(() => {
    if (!formData) return null;
    const entries = SIDEBAR_SECTIONS
      .filter((s) => formData[s] !== undefined)
      .map((s) => [s, filterHiddenFields(formData[s])]);
    return entries.length > 0 ? Object.fromEntries(entries) : null;
  }, [formData]);

  const generalFormData = React.useMemo(() => {
    if (!formData) return null;
    const entries = Object.entries(formData)
      .filter(([key]) => !SIDEBAR_SECTIONS.includes(key))
      .map(([key, value]) => [key, filterHiddenFields(value)]);
    return entries.length > 0 ? Object.fromEntries(entries) : null;
  }, [formData]);

  return (
    <div className="bg-gray-50 p-6 min-h-screen">
      <div className="flex gap-6 max-w-7xl mx-auto">
        {/* LEFT SIDEBAR */}
        <div className="w-full lg:w-4/12 text-left">
          <Card className="sticky top-6">
            <CardBody className="space-y-4">
             <div
  className="flex items-start justify-between gap-3 cursor-pointer hover:bg-gray-100 p-2 rounded-lg transition"
  onClick={() => navigateToProfile(deal.acceptedBy?.id)}
>
  <div className="flex items-center gap-3">
    <Avatar size="lg" src={deal.acceptedBy?.profileImage} />
    <div>
      <h4 className="font-semibold">
        {deal.acceptedBy?.firstName} {deal.acceptedBy?.lastName}
      </h4>
      <Chip size="sm" color="primary" variant="flat">
        {t("dealAccepter")}
      </Chip>
    </div>
  </div>

  {deal.agreementFile?.fileUrl && (
    <Tooltip
      content={t("downloadAgreement") || "Download agreement"}
      placement="bottom"
    >
      <Button
        size="sm"
        color="primary"
        variant="flat"
        isLoading={downloadingAgreement}
        onClick={(e) => {
          e.stopPropagation(); 
          handleDownloadAgreement();
        }}
        className="min-w-0 p-2"
      >
        <ArrowDownTrayIcon className="w-5 h-5" />
      </Button>
    </Tooltip>
  )}
</div>

              <Divider />

              {sidebarFormData ? (
                <div
                  className="
                    text-[14px] leading-6 text-gray-700
                    [&_label]:text-[14px] [&_label]:font-medium [&_label]:text-gray-500
                    [&_p]:text-[14px] [&_span]:text-[14px] [&_li]:text-[14px]
                    [&_div]:text-[14px] [&_input]:text-[14px]
                    [&_select]:text-[14px] [&_textarea]:text-[14px]
                  "
                >
                  {renderSummary(sidebarFormData)}
                </div>
              ) : (
                <div className="text-sm text-gray-500">{t("noFormData")}</div>
              )}
            </CardBody>
          </Card>
        </div>

        <div className="w-full lg:w-8/12 text-left">
          <div className="bg-white border-b flex items-center">
            {tabs.map((tabKey) => (
              <button
                key={tabKey}
                onClick={() => setActiveTab(tabKey)}
                className={`px-6 py-3 ${
                  activeTab === tabKey
                    ? "text-blue-600 border-b-2 border-blue-600"
                    : "text-gray-600"
                }`}
              >
                {t(tabKey)}
              </button>
            ))}

            {showResultTab && (
              <button
                onClick={() => setActiveTab("result")}
                className={`px-6 py-3 ${
                  activeTab === "result"
                    ? "text-blue-600 border-b-2 border-blue-600"
                    : "text-gray-600"
                }`}
              >
                {t("result")}
              </button>
            )}

            {deal.ratingResponse && deal.ratingResponse.length > 0 && (
              <button
                onClick={() => setActiveTab("ratings")}
                className={`px-6 py-3 ${
                  activeTab === "ratings"
                    ? "text-blue-600 border-b-2 border-blue-600"
                    : "text-gray-600"
                }`}
              >
                {t("ratings")}
              </button>
            )}

          </div>

          <div className="bg-white p-6 min-h-[600px] text-left">
            {["general", "progress", "activity"].includes(activeTab) && (
              <GeneralActivityTab
                deal={deal}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                t={t}
                formData={generalFormData}
                renderDynamicObject={renderDynamicObject}
                currentUserRole={currentUserRole}
                refreshDeal={refreshDeal}
                tr={tr}   
              />
            )}

            {activeTab === "componentPayments" && (
              <ComponentPaymentsTab
                key={JSON.stringify(
                  deal?.componentPayments?.map((p: any) => p.status),
                )}
                deal={deal}
                formData={formData}
                isFighter={isFighter}
                canPayComponents={canPayComponents}
                handlePayComponent={handlePayComponent}
                t={t}
              />
            )}

            {((activeTab === "result" && showResultTab) ||
              activeTab === "ratings") && (
              <ResultRatingsTab
                deal={deal}
                activeTab={activeTab}
                currentUserId={currentUserId || ""}
                isManager={isManager}
                hasUserRated={hasUserRated}
                refreshDeal={refreshDeal}
                tr={tr}   
              />
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default DealViewContent;
